import React, { useRef, useState } from "react";
import classNames from "classnames";
import { Field, ErrorMessage } from "formik";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import AsyncSelect from "react-select/async";
import AsyncCreatableSelect from "react-select/async-creatable";
import { getFollowUpQuestionData } from "./services/form";
import { generateFormField } from "./services/form";

/**
 ** Select field with label and error message.
 ** Supported parameters:
 **
 ** props: {
 **               'name': String,
 **               'placeholder': String,
 **               'label': String,
 **               'option': Object [],
 **               'onChange': Function,
 **               'value': Object {},
 **             }
 **
 ** Note: You need a list of objects as options with label and value element.
 **/

const customStyles = (theme) => ({
  ...theme,
  boxShadow: "inset 0 0.0625em 0.125em rgba(0, 0, 0, 0.05)",
  colors: {
    ...theme.colors,
    primary: "#6ea9a9",
    primary25: "#cfe1e1",
    primary50: "#9ec5c5",
  },
});

const SELECT_CLASS_NAME = "react-select-container";
const SELECT_CLASS_NAME_PREFIX = "react-select";

const FormSelect = (props) => {
  //* destructure props
  let {
    name,
    placeholder,
    onChange,
    isSelectable,
    followUpQuestions = [],
    options,
    value,
    hospitals,
  } = props;

  let finalOptions = options?.map((option) => {
    if (typeof option?.value === "string") {
      let finalOption = option?.value?.includes("???")
        ? {
            label: option?.value?.split("???")?.[0],
            value: option?.value,
          }
        : option;
      return finalOption;
    }
    return option;
  });

  const fieldRef = useRef(null);
  const [helperValue, setHelperValue] = useState(null);
  if (name.toLowerCase().includes("prescribinghospital"))
    finalOptions = hospitals.map((hospital) => ({
      value: hospital,
      label: hospital,
    }));

  //* Function to set the value of the react-select in
  //* formik values.
  //*
  //* Note: Curried Function.
  //*       Need to call handleChange(form) to return (selectedValue) => { ... }
  const handleChange = (form) => (selectedValue) => {
    const [finalValue, finalHelper] =
      typeof selectedValue?.value === "string" &&
      selectedValue?.value?.includes("???")
        ? [
            {
              label: selectedValue?.value?.split("???")[0],
              value: selectedValue?.value?.split("???")[0],
            },
            selectedValue?.value?.split("???")[1],
          ]
        : [selectedValue, null];

    if (finalHelper != null) {
      setHelperValue(finalHelper);
    }

    form.setFieldValue(name, finalValue);
    if (onChange) onChange(finalValue, form.setFieldValue);
  };

  const handleOnKeyDown = (event) => {
    if (
      name === "programServiceRating" &&
      !/[\d\b]/.test(event.key) &&
      event.key !== "Backspace" &&
      event.key !== "Enter" &&
      event.key !== "ArrowLeft" &&
      event.key !== "ArrowRight"
    ) {
      event.preventDefault();
    }
  };

  const SelectComponent = ({ form }) => {
    const Selector = props.isAsync ? AsyncSelect : Select;
    return (
      <Selector
        {...props}
        options={finalOptions}
        className={SELECT_CLASS_NAME}
        classNamePrefix={SELECT_CLASS_NAME_PREFIX}
        onChange={handleChange(form)}
        theme={customStyles}
        placeholder={placeholder}
        onKeyDown={handleOnKeyDown}
        aria-label={name}
      />
    );
  };

  const CreatableSelectComponent = ({ form }) => {
    const Selector = props.isAsync ? AsyncCreatableSelect : CreatableSelect;
    return (
      <Selector
        {...props}
        options={finalOptions}
        className={SELECT_CLASS_NAME}
        classNamePrefix={SELECT_CLASS_NAME_PREFIX}
        onChange={handleChange(form)}
        theme={customStyles}
        isDisabled={props.isDisabled}
        placeholder={placeholder}
        onKeyDown={handleOnKeyDown}
        aria-label={name}
      />
    );
  };

  return (
    <div className="field">
      {props.label && (
        <label
          className={classNames("label has-text-weight-normal")}
          ref={fieldRef}
        >
          {!!props.labelIcon && (
            <span className={`icon has-text-${props.labelIconColor}`}>
              {props.labelIcon}
            </span>
          )}
          {props.label}
          {!props.isRequired && !props.hideOptional && (
            <span className="has-text-grey is-italic"> (Optional)</span>
          )}
          {!!props.helper && (
            <span
              className={classNames(
                "helper has-text-weight-normal",
                props.helperClassName
              )}
            >
              <br />
              {props.helper}
            </span>
          )}
          {!!helperValue && (
            <span
              className={classNames(
                "helper has-text-weight-normal",
                props.helperClassName
              )}
            >
              <br />
              {helperValue}
            </span>
          )}
        </label>
      )}
      <Field>
        {({ form }) => (
          <div>
            {isSelectable && <CreatableSelectComponent form={form} />}
            {!isSelectable && <SelectComponent form={form} />}
            {followUpQuestions.length > 0 &&
              followUpQuestions.map((followUpQuestion) => {
                const getFormField = getFollowUpQuestionData({
                  followUpQuestion,
                  formFields: props.formFields,
                });
                if (getFormField?.referenceAnswer === value.value) {
                  return (
                    <div className="notification is-light ml-2 mt-1">
                      {generateFormField({
                        formField: {
                          ...getFormField,
                        },
                        formFields: props.formFields,
                        values: props.formValues,
                        setFieldValue: props.setFieldValue,
                        isFollowUpQuestion: true,
                      })}
                    </div>
                  );
                }
              })}
          </div>
        )}
      </Field>
      <p className="helper has-text-danger">
        <ErrorMessage name={!!props?.isMulti ? name : `${name}.value`} />
      </p>
    </div>
  );
};

export default FormSelect;
